import React, { ReactElement, useEffect, useState } from 'react';
import { Box, Button, Dialog, IconButton, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';

import { gray40, gray80, white, tertiaryButtonBackgroundColor } from '../../../constants/colors';
import { useProxyDialogSx } from '../../../hooks/useProxyDialogSx';
import { boldMd, regularXl, regularLg } from '../../../constants/location';
import FileNameWithExtensionField from '../form/FileNameWithExtensionField';
import { splitFileName, validateFileName } from '../../../utils/fileNameValidation';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (newFileName: string) => void;
  /** 現在のファイル名（拡張子付き） */
  currentFileName: string;
};

const styles = {
  dialog: {
    '& .MuiPaper-root': {
      border: '1px solid' + gray40,
      borderRadius: '8px',
      boxSizing: 'border-box',
      margin: '16px',
      padding: '24px',
      width: '480px',
      maxWidth: '90vw',
      '@media screen and (max-width: 960px)': {
        padding: '24px 16px',
      },
    },
  },
  header: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  title: {
    ...regularXl,
    '@media screen and (max-width:960px)': {
      ...regularLg,
    },
  },
  iconButton: {
    padding: 0,
  },
  icon: {
    height: '32px',
    width: '32px',
  },
  inputRow: {
    marginBottom: '24px',
  },
  buttonWrapper: {
    display: 'flex',
    gap: '16px',
  },
  cancelButton: {
    ...tertiaryButtonBackgroundColor,
    height: '44px',
    flex: 1,
    minWidth: 0,
  },
  cancelButtonText: {
    ...boldMd,
    color: gray80,
  },
  saveButton: {
    height: '44px',
    flex: 1,
    minWidth: 0,
    backgroundColor: '#FF6B35',
    '&:hover': {
      backgroundColor: '#E55A2B',
    },
    '&:disabled': {
      backgroundColor: '#FFB299',
      cursor: 'not-allowed',
    },
  },
  saveButtonText: {
    ...boldMd,
    color: white,
  },
};

const FileExportRenameDialog = ({
  open,
  onClose,
  onSave,
  currentFileName,
}: Props): ReactElement => {
  const proxyDialogSx = useProxyDialogSx();
  const { base: initialBase, ext } = splitFileName(currentFileName);
  const [fileNameBase, setFileNameBase] = useState(initialBase);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      const split = splitFileName(currentFileName);
      setFileNameBase(split.base);
      setTouched(false);
    }
  }, [open, currentFileName]);

  const errorMessage = touched ? validateFileName(fileNameBase) : undefined;
  const isValid = validateFileName(fileNameBase) === undefined;
  const isChanged = fileNameBase.trim() !== initialBase;

  const handleSave = (): void => {
    if (!isValid) return;
    onSave(`${fileNameBase.trim()}${ext}`);
  };

  return (
    <Dialog open={open} onClose={onClose} sx={[styles.dialog, proxyDialogSx]}>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>ファイル名を変更</Typography>
        <IconButton onClick={onClose} sx={styles.iconButton}>
          <Close sx={styles.icon} />
        </IconButton>
      </Box>

      <Box sx={styles.inputRow}>
        <FileNameWithExtensionField
          value={fileNameBase}
          onChange={(e): void => {
            setFileNameBase(e.target.value);
            if (!touched) setTouched(true);
          }}
          onBlur={(): void => setTouched(true)}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          extension={ext}
        />
      </Box>

      <Box sx={styles.buttonWrapper}>
        <Button sx={styles.cancelButton} onClick={onClose}>
          <Typography sx={styles.cancelButtonText}>キャンセル</Typography>
        </Button>
        <Button sx={styles.saveButton} onClick={handleSave} disabled={!isValid || !isChanged}>
          <Typography sx={styles.saveButtonText}>保存する</Typography>
        </Button>
      </Box>
    </Dialog>
  );
};

export default FileExportRenameDialog;
